import { KnownId } from "@fairfleet/geotab";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";

/** Geotab adds GPS distance to the odometer continuously; minutes do not change the figure much. */
const STALE_MS = 5 * 60 * 1000;

/**
 * The odometer Geotab keeps from GPS, as MyGeotab shows it with "(verwendet jetzt GPS)".
 *
 * Kept as `DiagnosticOdometerAdjustmentId`: real readings are only the rare manual adjustments,
 * the figure in between is Geotab's interpolation. A search from and to the same instant returns
 * that interpolated figure (checked against MyGeotab on 2026-10-27, bus 669 regiobus: 56336 km).
 * One call.
 *
 * @param at - The end of the period the page shows; now where left out.
 */
export function useGpsOdometerStatusData(deviceId: string | undefined, at?: Date) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["gpsOdometerStatusData", activeSessionId, deviceId, at?.getTime()],
    queryFn: async ({ signal }) => {
      const instant = at ?? new Date();
      return geotabClient.get(
        "StatusData",
        {
          deviceSearch: { id: deviceId },
          diagnosticSearch: { id: KnownId.DiagnosticOdometerAdjustmentId },
          fromDate: instant,
          toDate: instant,
        },
        1,
        signal,
      );
    },
    enabled: !!deviceId,
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
}
